import * as R from 'ramda';
import React, { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router';

import { CommandGroup } from 'components/commandPalette/base/CommandGroup';
import Icon from 'components/lib/ui/Icon';

import { useGetCommandsByGroup, useRegisterContextualCommands } from 'lib/commands/context';
import type { MonarchCommand, MonarchCommandGroup } from 'lib/commands/types';
import { ShortcutType } from 'lib/keyboardShortcuts';

import type { SidebarRouteName } from 'constants/sidebar';
import { SIDEBAR_ROUTES } from 'constants/sidebar';

const GROUP: MonarchCommandGroup = 'navigation';
const ROUTE_NAMES = Object.keys(SIDEBAR_ROUTES);
const SIDEBAR_ROUTE_TO_SHORTCUT_MAP: Partial<Record<SidebarRouteName, string[]>> = {
  Dashboard: ['g', 'd'],
  Transactions: ['g', 't'],
  Reports: ['g', 'r'],
  Budget: ['g', 'b'],
  Recurring: ['g', 'c'],
  Accounts: ['g', 'a'],
  'Cash Flow': ['g', 'f'],
  Goals: ['g', 'g'],
  Investments: ['g', 'i'],
};

/**
 * A component that renders all navigation-related commands in the command palette.
 */
export const NavigationCommands: React.FC = () => {
  const commands = useGetCommandsByGroup(GROUP);

  return (
    <CommandGroup.Root group={GROUP} heading="Navigation">
      {commands.map((command) => (
        <CommandGroup.Item key={command.id} {...command} />
      ))}
    </CommandGroup.Root>
  );
};

/**
 * A hook that registers all navigation-related commands.
 * Separate from the UI rendering component.
 */
export const useRegisterNavigationCommands = (): void => {
  const history = useHistory();
  const { pathname } = history.location;

  const maybeNavigateToPath = useCallback(
    (path: string) => {
      if (pathname !== path) {
        history.push(path);
        return true;
      }

      return false;
    },
    [pathname],
  );

  const shouldRegisterNavigationCommand = useCallback(
    (path: string) => !pathname.startsWith(path),
    [pathname],
  );

  const routeToCommand = useCallback(
    ([route, { route: path, icon }]: [string, (typeof SIDEBAR_ROUTES)[SidebarRouteName]]) =>
      ({
        id: route.toLowerCase(),
        label: `Go to ${route}`,
        keys: SIDEBAR_ROUTE_TO_SHORTCUT_MAP[route as SidebarRouteName],
        action: () => maybeNavigateToPath(path),
        icon: <Icon name={icon} />,
        shortcutType: ShortcutType.SEQUENCE,
        shouldRegister: shouldRegisterNavigationCommand(path),
        group: GROUP,
        analyticsMetadata: {
          analyticsId: `navigate-to-${route.toLowerCase()}`,
        },
      }) satisfies MonarchCommand,
    [maybeNavigateToPath, shouldRegisterNavigationCommand],
  );

  const commands = useMemo(
    () => {
      const routePairs = R.toPairs(SIDEBAR_ROUTES);
      return sortRoutes(routePairs).filter(filterRoute).map(routeToCommand);
    },
    // pathname is intentionally included in the dependency array so we can re-register commands when the route changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [routeToCommand, pathname],
  );

  useRegisterContextualCommands(commands);
};

const sortRoutes = (routes: [string, { route: string; icon: string }][]) =>
  R.sort((a, b) => {
    const aIndex = ROUTE_NAMES.indexOf(a[0]);
    const bIndex = ROUTE_NAMES.indexOf(b[0]);
    return aIndex - bIndex;
  }, routes);

/** Only register commands for routes that have a shortcut. */
const filterRoute = ([route]: [string, { route: string; icon: string }]) => {
  const sidebarRouteKeys = R.keys(SIDEBAR_ROUTE_TO_SHORTCUT_MAP);
  return sidebarRouteKeys.includes(route as SidebarRouteName);
};
